import {
  DndContext,
  closestCenter,
  DragEndEvent,
  DragOverlay
} from "@dnd-kit/core"
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove
} from "@dnd-kit/sortable"
import { useFormContext, useWatch } from "react-hook-form"
import { SortableCapsuleRow } from "./SortableCapsuleRow"
import { useState, memo } from "react"

export const CapsuleItemList = memo(() => {
  const { control, setValue } = useFormContext()
  const shows = useWatch({ control, name: "selectedShows", defaultValue: [] })
  const collections = useWatch({ control, name: "selectedCollections", defaultValue: [] })
  const categories = useWatch({ control, name: "selectedCategories", defaultValue: [] })

  // Combine all items into a single array with unique IDs
  const allItems = [
    ...shows.map(item => ({ ...item, itemType: 'show' })),
    ...collections.map(item => ({ ...item, itemType: 'collection' })),
    ...categories.map(item => ({ ...item, itemType: 'category' }))
  ]

  const [activeItem, setActiveItem] = useState<any>(null)

  const handleDelete = (id: string, itemType: string) => {
    if (itemType === 'show') {
      setValue("selectedShows", shows.filter(item => item.id !== id), { shouldDirty: true })
    } else if (itemType === 'collection') {
      setValue("selectedCollections", collections.filter(item => item.id !== id), { shouldDirty: true })
    } else if (itemType === 'category') {
      setValue("selectedCategories", categories.filter(item => item.id !== id), { shouldDirty: true })
    }
  }

  const moveItem = (from: number, to: number) => {
    if (to < 0 || to >= allItems.length) return
    
    const reordered = arrayMove(allItems, from, to)
    
    // Split back into separate arrays
    const newShows = reordered.filter(item => item.itemType === 'show').map(({ itemType, ...rest }) => rest)
    const newCollections = reordered.filter(item => item.itemType === 'collection').map(({ itemType, ...rest }) => rest)
    const newCategories = reordered.filter(item => item.itemType === 'category').map(({ itemType, ...rest }) => rest)
    
    setValue("selectedShows", newShows, { shouldDirty: true })
    setValue("selectedCollections", newCollections, { shouldDirty: true })
    setValue("selectedCategories", newCategories, { shouldDirty: true })
  }

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    setActiveItem(null)

    if (!over || active?.id === over?.id) return

    const oldIndex = allItems.findIndex(item => item?.id === active?.id)
    const newIndex = allItems.findIndex(item => item?.id === over?.id)

    moveItem(oldIndex, newIndex)
  }

  if (allItems.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">
        No items selected. Use the search fields above to add shows, collections, or categories.
      </div>
    )
  }

  return (
    <DndContext
      collisionDetection={closestCenter}
      onDragStart={e => {
        setActiveItem(allItems.find(item => item.id === e.active.id))
      }}
      onDragEnd={onDragEnd}
    >
      <SortableContext
        items={allItems.map(item => item.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className="max-h-[400px] overflow-y-auto space-y-3 pr-2 border rounded p-6 dark:border-gray-600">
          {allItems.map((item, index) => (
            <SortableCapsuleRow
              key={item?.id}
              item={item}
              index={index}
              total={allItems.length}
              onDelete={handleDelete}
              onMoveUp={() => moveItem(index, index - 1)}
              onMoveDown={() => moveItem(index, index + 1)}
            />
          ))}
        </div>
      </SortableContext>

      <DragOverlay>
        {activeItem ? (
          <div className="p-3 border rounded bg-white dark:bg-slate-700 shadow-lg">
            {activeItem?.name}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  )
})
